import React, { useState, useEffect } from "react";
import { Timeline, Card, Tag, Typography, Spin, Empty, Space, Button, Collapse, Tooltip, Alert, Switch } from "antd";
import {
    CheckCircleOutlined,
    CloseCircleOutlined,
    ClockCircleOutlined,
    LoadingOutlined,
    SyncOutlined,
    FileTextOutlined,
    CalculatorOutlined,
    SafetyCertificateOutlined,
    ToolOutlined,
    BugOutlined,
    ReloadOutlined,
    InfoCircleOutlined,
} from "@ant-design/icons";
import { settlementService } from "../../../../api/enhancedApi";
import { formatDateTime } from "../../../../utils/dateUtils";

const { Text, Paragraph } = Typography;
const { Panel } = Collapse;

/**
 * Agent类型到图标的映射
 */
const AGENT_ICONS = {
    OCR_AGENT: <FileTextOutlined style={{ color: "#1890ff" }} />,
    AP_INVOICE_PARSER_AGENT: <FileTextOutlined style={{ color: "#52c41a" }} />,
    AP_COST_ALLOCATION_AGENT: <CalculatorOutlined style={{ color: "#722ed1" }} />,
    AP_FLIGHT_COST_VALIDATOR_AGENT: <SafetyCertificateOutlined style={{ color: "#fa8c16" }} />,
    AP_INVOICE_FIXER_AGENT: <ToolOutlined style={{ color: "#eb2f96" }} />,
    AP_INVOICE_BATCH_VALIDATION_AGENT: <SafetyCertificateOutlined style={{ color: "#13c2c2" }} />,
};

/**
 * 状态到颜色的映射
 */
const STATUS_COLORS = {
    PENDING_AI: "processing",
    PENDING_REVIEW: "warning",
    PROCESSING: "processing",
    COMPLETED: "success",
    FAILED: "error",
    REJECTED: "default",
};

/**
 * 状态到Timeline颜色的映射
 */
const TIMELINE_COLORS = {
    PENDING_AI: "blue",
    PENDING_REVIEW: "orange",
    PROCESSING: "blue",
    COMPLETED: "green",
    FAILED: "red",
    REJECTED: "gray",
};

/**
 * 格式化时长
 */
const formatDuration = (seconds) => {
    if (seconds === null || seconds === undefined) return "-";
    if (seconds < 60) return `${seconds}秒`;
    if (seconds < 3600) return `${Math.floor(seconds / 60)}分${seconds % 60}秒`;
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    return `${hours}小时${minutes}分`;
};

/**
 * 获取状态图标
 */
const getStatusIcon = (status) => {
    switch (status) {
        case "COMPLETED":
            return <CheckCircleOutlined style={{ color: "#52c41a" }} />;
        case "FAILED":
            return <CloseCircleOutlined style={{ color: "#ff4d4f" }} />;
        case "PENDING_REVIEW":
            return <ClockCircleOutlined style={{ color: "#faad14" }} />;
        case "PROCESSING":
        case "PENDING_AI":
            return <LoadingOutlined style={{ color: "#1890ff" }} spin />;
        case "REJECTED":
            return <CloseCircleOutlined style={{ color: "#8c8c8c" }} />;
        default:
            return <SyncOutlined style={{ color: "#1890ff" }} />;
    }
};

/**
 * 处理历史记录面板组件
 *
 * 显示InvoiceDataSource关联的所有AI ActionItems的执行历史
 */
const ProcessingHistoryPanel = ({ record, onRefresh }) => {
    const [loading, setLoading] = useState(false);
    const [historyData, setHistoryData] = useState(null);
    const [error, setError] = useState(null);
    const [showDebug, setShowDebug] = useState(false);

    // 加载处理历史
    const loadHistory = async () => {
        if (!record?.id) return;

        setLoading(true);
        setError(null);

        try {
            const response = await settlementService.getProcessingHistory(record.id, showDebug);
            setHistoryData(response.data || response);
        } catch (err) {
            console.error("加载处理历史失败:", err);
            setError(err.message || "加载失败");
        } finally {
            setLoading(false);
        }
    };

    // 初始加载和依赖变化时重新加载
    useEffect(() => {
        loadHistory();
    }, [record?.id, showDebug]);

    // 渲染单个历史项
    const renderHistoryItem = (item, index) => {
        const icon = AGENT_ICONS[item.agent_type] || <InfoCircleOutlined />;
        const color = TIMELINE_COLORS[item.status] || "gray";

        return (
            <Timeline.Item key={item.id || index} color={color} dot={getStatusIcon(item.status)}>
                <Card
                    size="small"
                    style={{ marginBottom: 8 }}
                    bodyStyle={{ padding: "12px 16px" }}
                    hoverable
                >
                    {/* 标题行 */}
                    <Space style={{ width: "100%", justifyContent: "space-between", marginBottom: 8 }}>
                        <Space>
                            {icon}
                            <Text strong>{item.agent_display_name}</Text>
                            <Tag color={STATUS_COLORS[item.status]}>{item.status_display_name}</Tag>
                            {item.resolution_display_name && (
                                <Tag color="blue">{item.resolution_display_name}</Tag>
                            )}
                        </Space>
                        <Text type="secondary" style={{ fontSize: 12 }}>
                            #{item.id}
                        </Text>
                    </Space>

                    {/* 摘要信息 */}
                    {item.summary && (
                        <Paragraph style={{ marginBottom: 8, color: "#595959" }}>
                            {item.summary}
                        </Paragraph>
                    )}

                    {/* 时间信息 */}
                    <Space size="large" style={{ fontSize: 12 }}>
                        <Text type="secondary">
                            开始: {item.created_at ? formatDateTime(item.created_at) : "-"}
                        </Text>
                        {item.duration_seconds !== null && (
                            <Text type="secondary">
                                耗时: <Text style={{ color: "#1890ff" }}>{formatDuration(item.duration_seconds)}</Text>
                            </Text>
                        )}
                        {item.resolved_at && (
                            <Text type="secondary">
                                完成: {formatDateTime(item.resolved_at)}
                            </Text>
                        )}
                    </Space>

                    {/* AI处理备注 */}
                    {item.ai_processing_notes && (
                        <div style={{ marginTop: 8, padding: "8px 12px", background: "#f5f5f5", borderRadius: 4 }}>
                            <Text type="secondary" style={{ fontSize: 12 }}>AI备注: </Text>
                            <Text style={{ fontSize: 12 }}>{item.ai_processing_notes}</Text>
                        </div>
                    )}

                    {/* 用户备注 */}
                    {item.user_remarks && (
                        <div style={{ marginTop: 8, padding: "8px 12px", background: "#e6f7ff", borderRadius: 4 }}>
                            <Text type="secondary" style={{ fontSize: 12 }}>用户备注: </Text>
                            <Text style={{ fontSize: 12 }}>{item.user_remarks}</Text>
                        </div>
                    )}

                    {/* 调试信息（折叠显示） */}
                    {showDebug && item.debug_info && (
                        <Collapse
                            ghost
                            size="small"
                            style={{ marginTop: 8 }}
                            items={[
                                {
                                    key: "debug",
                                    label: (
                                        <Space>
                                            <BugOutlined />
                                            <Text type="secondary" style={{ fontSize: 12 }}>调试信息</Text>
                                        </Space>
                                    ),
                                    children: (
                                        <pre style={{
                                            fontSize: 11,
                                            background: "#fafafa",
                                            padding: 8,
                                            borderRadius: 4,
                                            maxHeight: 200,
                                            overflow: "auto",
                                            margin: 0,
                                        }}>
                                            {JSON.stringify(item.debug_info, null, 2)}
                                        </pre>
                                    ),
                                },
                            ]}
                        />
                    )}
                </Card>
            </Timeline.Item>
        );
    };

    // 渲染主体内容
    const renderContent = () => {
        if (loading) {
            return (
                <div style={{ textAlign: "center", padding: "40px 0" }}>
                    <Spin tip="加载处理历史..." />
                </div>
            );
        }

        if (error) {
            return (
                <Alert
                    type="error"
                    message="加载失败"
                    description={error}
                    showIcon
                    action={
                        <Button size="small" onClick={loadHistory}>
                            重试
                        </Button>
                    }
                />
            );
        }

        if (!historyData || !historyData.history || historyData.history.length === 0) {
            return (
                <Empty
                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                    description="暂无处理历史"
                    style={{ padding: "40px 0" }}
                />
            );
        }

        return (
            <div>
                {/* 统计信息 */}
                <div style={{ marginBottom: 16, padding: "12px 16px", background: "#fafafa", borderRadius: 4 }}>
                    <Space size="large">
                        <Text>
                            当前阶段: <Tag color="blue">{historyData.current_stage || "-"}</Tag>
                        </Text>
                        <Text>
                            处理记录: <Text strong>{historyData.total_items}</Text> 条
                        </Text>
                    </Space>
                </div>

                {/* 时间线 */}
                <Timeline mode="left" style={{ marginTop: 16 }}>
                    {historyData.history.map((item, index) => renderHistoryItem(item, index))}
                </Timeline>
            </div>
        );
    };

    return (
        <div>
            {/* 操作栏 */}
            <Space style={{ marginBottom: 16, width: "100%", justifyContent: "space-between" }}>
                <Space>
                    <Button
                        icon={<ReloadOutlined />}
                        onClick={loadHistory}
                        loading={loading}
                        size="small"
                    >
                        刷新
                    </Button>
                </Space>
                <Space>
                    {historyData?.debug_enabled && (
                        <Tooltip title="开启后显示详细的调试信息，用于开发阶段排查问题">
                            <Space>
                                <BugOutlined style={{ color: showDebug ? "#1890ff" : "#bfbfbf" }} />
                                <Switch
                                    size="small"
                                    checked={showDebug}
                                    onChange={setShowDebug}
                                />
                                <Text type="secondary" style={{ fontSize: 12 }}>调试模式</Text>
                            </Space>
                        </Tooltip>
                    )}
                </Space>
            </Space>

            {/* 主体内容 */}
            {renderContent()}
        </div>
    );
};

export default ProcessingHistoryPanel;
